import i18n, { validationSummary } from "@/core/i18n";
import type errors from "@repo/i18n/locales/en/errors.json";
import { ApiErrorCode, type ApiErrorBody } from "@repo/schema";
import type { TFunction } from "i18next";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { isApiError } from "./apiError";

type ErrorsKey = keyof typeof errors;

/**
 * The text to show for an API error body: the `errors` catalog entry for its
 * code, interpolated with its params, falling back to the API's own English
 * message for codes the catalog does not know (`UNKNOWN`, upstream codes).
 * A Zod failure lists its issues, worded like the client-side ones.
 */
const render = (t: TFunction<"errors">, body: ApiErrorBody): string =>
  body.code === ApiErrorCode.VALIDATION_FAILED && body.errors?.length
    ? validationSummary(body.errors)
    : t(body.code as ErrorsKey, { defaultValue: body.message, ...body.params });

const resolve = (t: TFunction<"errors">, err: unknown, fallback: string): string =>
  isApiError(err) ? render(t, err.data) : fallback;

/**
 * `(err, fallback) => string` for components: the localized message of an API
 * rejection, or `fallback` when the rejection is not one (network failure,
 * client-side throw). Re-renders with the language.
 */
export const useApiErrorMessage = () => {
  const { t } = useTranslation("errors");
  return useCallback((err: unknown, fallback: string) => resolve(t, err, fallback), [t]);
};

/** Same as the hook, for code outside React (listeners, stream clients, handlers). */
export const apiErrorMessage = (err: unknown, fallback: string): string =>
  resolve(i18n.getFixedT(null, "errors"), err, fallback);

/** For bodies that did not arrive as a rejection, e.g. a stream's `error` event. */
export const apiErrorBodyMessage = (body: ApiErrorBody): string =>
  render(i18n.getFixedT(null, "errors"), body);
